// src/pages/admin/BusinessDetailsPage.js
import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { Loader, AlertCircle, MapPin, Phone, Save } from 'lucide-react';

const BusinessDetailsPage = () => {
  const [details, setDetails] = useState({
    address: '',
    number: '',
    mail:''
  });
  const [pendingChanges, setPendingChanges] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveStatus, setSaveStatus] = useState({ show: false, message: '', type: '' });

  useEffect(() => {
    loadBusinessDetails();
  }, []);

  const loadBusinessDetails = async () => {
    try {
      const docRef = doc(db, 'businessDetails','kioGnL9sTArCVO1W54OL');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        setDetails(docSnap.data());
      } else {
        // אם המסמך לא קיים, ניצור אחד חדש עם ערכים ריקים
        setDetails({
          address: '',
          mail:'',
          number: ''
        });
      }
    } catch (err) {
      setError('אירעה שגיאה בטעינת הנתונים');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setPendingChanges(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    if (!pendingChanges) return;

    setSaving(true);
    setSaveStatus({ show: true, message: 'שומר שינויים...', type: 'info' });

    try {
      const docRef = doc(db, 'businessDetails','kioGnL9sTArCVO1W54OL');
      const updatedDetails = {
        ...details,
        ...pendingChanges
      };

      await updateDoc(docRef, updatedDetails);
      setDetails(updatedDetails);
      setPendingChanges(null);

      setSaveStatus({ show: true, message: 'נשמר בהצלחה!', type: 'success' });
      setTimeout(() => setSaveStatus({ show: false, message: '', type: '' }), 3000);
    } catch (err) {
      setSaveStatus({ show: true, message: 'שגיאה בשמירת הנתונים', type: 'error' });
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px] text-red-500">
        <AlertCircle className="w-6 h-6 mr-2" />
        <span>{error}</span>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-xl md:text-2xl font-bold">פרטי העסק</h1>
        {saveStatus.show && (
          <div className={`
            px-4 py-2 rounded-lg text-sm md:text-base
            ${saveStatus.type === 'success' ? 'bg-green-100 text-green-700' : ''}
            ${saveStatus.type === 'error' ? 'bg-red-100 text-red-700' : ''}
            ${saveStatus.type === 'info' ? 'bg-blue-100 text-blue-700' : ''}
          `}>
            {saveStatus.message}
          </div>
        )}
      </div>

      <div className="bg-white p-6 rounded-lg shadow-md space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <div className="flex items-center gap-2 mb-1">
              <MapPin className="w-4 h-4" />
              <span>כתובת</span>
            </div>
          </label>
          <input
            type="text"
            value={pendingChanges?.address ?? details.address}
            onChange={(e) => handleInputChange('address', e.target.value)}
            className="w-full p-3 border rounded-md"
            placeholder="הכנס כתובת..."
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <div className="flex items-center gap-2 mb-1">
              <MapPin className="w-4 h-4" />
              <span>אימייל</span>
            </div>
          </label>
          <input
            type="text"
            value={pendingChanges?.mail ?? details.mail}
            onChange={(e) => handleInputChange('mail', e.target.value)}
            className="w-full p-3 border rounded-md"
            placeholder="הכנס mail..."
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <div className="flex items-center gap-2 mb-1">
              <Phone className="w-4 h-4" />
              <span>מספר </span>
            </div>
          </label>
          <input
            type="tel"
            value={pendingChanges?.number ?? details.number}
            onChange={(e) => handleInputChange('number', e.target.value)}
            className="w-full p-3 border rounded-md"
            placeholder="הכנס מספר..."
            dir="ltr"
          />
        </div>

        {pendingChanges && (
          <button
            onClick={handleSave}
            disabled={saving}
            className={`
              w-full md:w-auto px-6 py-3 rounded-md
              flex items-center justify-center space-x-2 space-x-reverse
              ${saving ? 'bg-blue-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}
              text-white font-medium transition-colors
            `}
          >
            <Save className="w-4 h-4" />
            <span>{saving ? 'שומר...' : 'שמור שינויים'}</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default BusinessDetailsPage;